import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import Icon from '@components/Icon';
import { useAppTheme } from '@context/ThemeContext';
import FollowUsersStack, { FollowUsersStackParamList } from '@navigation/FollowUsersStack';
import GroupStack, { GroupStackParamList } from '@navigation/GroupStack';
import HomeStack, { HomeStackParamList } from '@navigation/HomeStack';
import { OnboardedStackParamList } from '@navigation/OnboardedStack';
import { OnboardedStackRoute, OnboardedTabRoute } from '@navigation/Routes';
import { BottomTabBarButtonProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { PlatformPressable } from '@react-navigation/elements';
import { NavigatorScreenParams } from '@react-navigation/native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

export type OnboardedTabParamList = {
  [OnboardedTabRoute.Home]: NavigatorScreenParams<HomeStackParamList>;
  [OnboardedTabRoute.FollowUsers]: NavigatorScreenParams<FollowUsersStackParamList>;
  [OnboardedTabRoute.Groups]: NavigatorScreenParams<GroupStackParamList>;
  [OnboardedTabRoute.CreatePlan]: undefined;
};

const Tab = createBottomTabNavigator<OnboardedTabParamList>();

function CreatePlanTabScreen() {
  return null;
}

function CreatePlanTabButton({ style, ...props }: BottomTabBarButtonProps) {
  const appTheme = useAppTheme();
  const contentColor = appTheme.dark ? appTheme.colors.surface : appTheme.colors.accent;

  return (
    <PlatformPressable
      {...props}
      accessibilityRole="button"
      pressOpacity={0.82}
      style={[style, styles.createPlanButtonHitArea]}>
      <View
        style={[
          styles.createPlanButton,
          { backgroundColor: appTheme.colors.primary, borderRadius: appTheme.roundness },
        ]}>
        <Icon
          name="plus"
          size={18}
          color={contentColor}
          testID="navigation.HomeTab.Icon.CreatePlan"
        />
        <Text
          adjustsFontSizeToFit
          minimumFontScale={0.85}
          numberOfLines={1}
          style={[styles.createPlanButtonLabel, { color: contentColor }]}>
          Create
        </Text>
      </View>
    </PlatformPressable>
  );
}

export default function OnboardedTab() {
  const navigation = useNavigation<StackNavigationProp<OnboardedStackParamList>>();

  return (
    <Tab.Navigator
      id={undefined}
      initialRouteName={OnboardedTabRoute.Home}
      screenOptions={{ headerShown: false }}>
      <Tab.Screen
        name={OnboardedTabRoute.Home}
        component={HomeStack}
        options={{
          tabBarIcon: ({ color, size }) => {
            return (
              <Icon
                name="home-outline"
                size={size}
                color={color}
                testID="navigation.HomeTab.Icon.Home"
              />
            );
          },
        }}
      />
      <Tab.Screen
        name={OnboardedTabRoute.FollowUsers}
        component={FollowUsersStack}
        options={{
          title: 'People',
          tabBarIcon: ({ color, size }) => {
            return (
              <Icon
                name="account-plus-outline"
                size={size}
                color={color}
                testID="navigation.HomeTab.Icon.FollowUsers"
              />
            );
          },
        }}
      />
      <Tab.Screen
        name={OnboardedTabRoute.Groups}
        component={GroupStack}
        options={{
          tabBarIcon: ({ color, size }) => {
            return (
              <Icon
                name="account-multiple-outline"
                size={size}
                color={color}
                testID="navigation.HomeTab.Icon.Groups"
              />
            );
          },
        }}
      />
      <Tab.Screen
        name={OnboardedTabRoute.CreatePlan}
        component={CreatePlanTabScreen}
        options={{
          title: 'Create',
          tabBarAccessibilityLabel: 'Create plan',
          tabBarButtonTestID: 'navigation.HomeTab.Button.CreatePlan',
          tabBarButton: (props) => (
            <CreatePlanTabButton
              {...props}
              onPress={() => {
                navigation.navigate(OnboardedStackRoute.CreatePlan);
              }}
            />
          ),
          tabBarIcon: () => null,
          tabBarLabelVisibilityMode: 'unlabeled',
        }}
      />
    </Tab.Navigator>
  );
}

const styles = StyleSheet.create({
  createPlanButton: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 6,
    height: 38,
    justifyContent: 'center',
    maxWidth: '100%',
    minWidth: 76,
    paddingHorizontal: 8,
  },
  createPlanButtonHitArea: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  createPlanButtonLabel: {
    fontSize: 12,
    fontWeight: '700',
  },
});
